import { useDebounce } from "../../../../../hooks/useDebounce";
import { ChangeEvent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectTest } from "../../../../../store/test/repository/selector";
import { getSingleTest } from "../../../../../store/test/useCases/getSingleTest/action";
import { useParams } from "react-router-dom";
import { selectQuiz } from "../../../../../store/quiz/repository/selector";
import { getAttemptsForModeration } from "../../../../../store/quiz/useCases/getAttemptsForModeration/action";
import { moderateAnswer } from "../../../../../store/quiz/useCases/moderateAnswer/action";
import { getAnswers } from "../../../../../store/quiz/useCases/getAnswers/action";

const REQUEST_LIMIT = 30;

export type AnswersUserProps = {
  id: string;
  name: string;
  position: string;
  photo: string;
  endTime: string;
  attempts?: {
    id: number;
    quizId: number;
    userId: number;
    endTime: string;
  }[];
};

export const useAnswers = () => {
  const { singleTest } = useSelector(selectTest);
  const { isLoading, answers, answerLoading, moderationList } =
    useSelector(selectQuiz);
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();

  const [data, setData] = useState<AnswersUserProps[]>([]);
  const [searchValue, setSearchValue] = useState<string | null>(null);
  const [page, setPage] = useState<number>(0);

  const getData = (search = searchValue) => {
    dispatch(
      getAttemptsForModeration({
        id,
        offset: page * REQUEST_LIMIT,
        limit: REQUEST_LIMIT,
        search,
      })
    );
  };

  const searchRequestHandle = (value: string) => {
    getData(value);
  };

  const { debouncedWrapper } = useDebounce(searchRequestHandle);

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    debouncedWrapper(e.target.value);
  };

  const handlePageChange = (event: ChangeEvent<unknown>, page: number) => {
    setPage(page - 1);
  };

  const handleSetResult = (
    id: number,
    userQuestionId: number,
    isCorrect: boolean
  ) => {
    dispatch(moderateAnswer({ id, userQuestionId, isCorrect }));
  };

  const getCurrentAnswers = (id: string) => {
    if (!answers?.[id]) {
      dispatch(getAnswers({ id }));
    }
  };

  useEffect(() => {
    if (!id) return;
    getData();
  }, [id, page]);

  useEffect(() => {
    if (!id) return;
    dispatch(getSingleTest({ id }));
  }, [id]);

  useEffect(() => {
    if (moderationList.rows.length === 0) {
      setData([]);
      return;
    }

    setData(
      moderationList.rows.map(
        ({ first_name, last_name, patronymic, ...item }) => ({
          name: `${last_name} ${first_name} ${patronymic}`,
          ...item,
        })
      )
    );
  }, [moderationList]);

  return {
    isLoading,
    moderationList,
    data,
    singleTest,
    searchValue,
    handleSearch,
    handleSetResult,
    getCurrentAnswers,
    answerLoading,
    answers,
    page: page === 0 ? 1 : page + 1,
    pageAmount: Math.ceil(moderationList.count / REQUEST_LIMIT),
    handlePageChange,
  };
};
